import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "mail";

export interface MailAddress {
	name?: string;
	address?: string;
}

export interface MailFolder {
	path: string;
	name: string;
	specialUse?: string;
}

export interface MailMessageSummary {
	uid: string;
	subject: string;
	from: MailAddress[];
	to: MailAddress[];
	date: string | null;
	seen: boolean;
	size?: number;
}

export interface MailMessagePage {
	messages: MailMessageSummary[];
	total: number;
	page: number;
	limit: number;
}

export interface MailAttachmentInfo {
	filename: string;
	size: number;
	contentType: string;
	downloadable: boolean;
}

export interface MailMessageDetail {
	uid: string;
	subject: string;
	from: MailAddress[];
	to: MailAddress[];
	cc: MailAddress[];
	date: string | null;
	html: string | null;
	text: string | null;
	attachments: MailAttachmentInfo[];
}

export function useMailFolders() {
	return useQuery({
		queryKey: [KEY, "folders"],
		queryFn: () => api.get<{ folders: MailFolder[] }>("/api/mail/folders"),
		staleTime: 60_000,
		retry: false,
	});
}

export function useMailMessages(folder: string, page: number, limit = 25) {
	return useQuery({
		queryKey: [KEY, "list", folder, page, limit],
		queryFn: () =>
			api.get<MailMessagePage>(
				`/api/mail/messages?folder=${encodeURIComponent(folder)}&page=${page}&limit=${limit}`,
			),
		enabled: !!folder,
		retry: false,
	});
}

export function useMailMessage(folder: string, uid: string | null) {
	return useQuery({
		queryKey: [KEY, "detail", folder, uid],
		queryFn: () =>
			api.get<MailMessageDetail>(
				`/api/mail/messages/${uid}?folder=${encodeURIComponent(folder)}`,
			),
		enabled: !!folder && uid != null,
		retry: false,
	});
}
